import { useEffect, useRef, useState } from "react";
import { useControllers } from "../state/controllers";
import { usePlaybackStore } from "../state/playbackStore";
import { useEditorStore } from "../state/editorStore";
import { useProjectStore } from "../state/projectStore";
import { PlayIcon, PauseIcon, VolumeIcon, TargetIcon, MenuIcon, SongWaveIcon, ScoreLinesIcon, SaveIcon } from "./icons";
import { useT, useLang, LANGS } from "../i18n";
import { useAuth, renderGoogleButton, saveScore } from "../state/auth";
import { useUi } from "../state/ui";

// Transport: play/pause + current chord inline; volume & cursor-compensation live in a ⚙ settings
// popover to keep the topbar uncluttered (especially on mobile).
export function PlaybackControls() {
  const c = useControllers();
  const isPlaying = usePlaybackStore((s) => s.isPlaying);
  const volume = usePlaybackStore((s) => s.volume);
  const cursorComp = usePlaybackStore((s) => s.cursorComp);
  const setVolume = usePlaybackStore((s) => s.setVolume);
  const setCursorComp = usePlaybackStore((s) => s.setCursorComp);

  const playMode = usePlaybackStore((s) => s.playMode);
  const setPlayMode = usePlaybackStore((s) => s.setPlayMode);
  const t = useT();
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const openScores = useUi((s) => s.setScoresPage);
  const [open, setOpen] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saving, setSaving] = useState(false);     // inline name-entry mode
  const [saveTitle, setSaveTitle] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);
  const gbtnRef = useRef<HTMLDivElement>(null);

  // render the Google sign-in button when the menu is open and the user is logged out
  useEffect(() => {
    if (open && !user && gbtnRef.current) renderGoogleButton(gbtnRef.current).catch(() => {});
  }, [open, user]);

  const hasChart = useProjectStore((s) => s.chartMeasures.length > 0);

  // open the inline name editor with a sensible default ("(video/file title) 추출 악보")
  const startSave = () => {
    const ps = useProjectStore.getState();
    const base = (c.youtube.getTitle?.() || ps.title || "").trim() || t("scores.untitled");
    setSaveTitle(t("save.default", { base }));
    setSaving(true);
  };
  const confirmSave = async () => {
    const ps = useProjectStore.getState();
    if (!ps.analysis || !ps.chartMeasures.length) { setSaving(false); setSaveMsg(t("save.nochart")); return; }
    const data = { analysis: ps.analysis, source: ps.source, title: saveTitle, beatLevelMul: ps.beatLevelMul, arrangement: ps.arrangement };
    const res = await saveScore({ id: ps.savedId ?? undefined, title: saveTitle.trim() || t("scores.untitled"), data });
    setSaving(false);
    if (res.status === 200 && res.id) { ps.setSavedId(res.id); setSaveMsg(t("save.ok")); }
    else if (res.status === 403) { setSaveMsg(res.error || t("save.fail")); }
    else { setSaveMsg(t("save.fail")); }
    setTimeout(() => setSaveMsg(""), 3000);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  // song mode = the recording (Tone.Player); score mode = alphaTab's synth (notation + techniques,
  // no song). They never play together — switching modes stops both.
  const switchMode = (m: "song" | "score") => {
    if (m === playMode) return;
    // pause (not stop) both sources so the cursor stays put — stop() would rewind alphaTab to bar 1
    c.activeSource().pause(); c.score.pause();
    setPlayMode(m);
  };
  const toggle = async () => {
    if (playMode === "song") {
      const src = c.activeSource();
      if (src.isPlaying()) { src.pause(); return; }
      // edit mode: start playback from the selected measure
      const es = useEditorStore.getState();
      if (es.editMode && es.activeMeasure >= 0) {
        const m = useProjectStore.getState().chartMeasures[es.activeMeasure];
        if (m) src.seek(m.startTime);
      }
      await src.play();
    } else {
      if (c.score.playing) c.score.pause();
      else { c.score.resumeAudio(); c.score.play(); }
    }
  };

  return (
    <div className="transport">
      {hasChart && (
        <button className="play-btn" onClick={toggle} title={t("ctrl.play")} aria-label="play">
          {isPlaying ? <PauseIcon size={16} /> : <PlayIcon size={16} />}
        </button>
      )}
      {hasChart && (
        <div className="mode-toggle" role="group" aria-label={t("ctrl.play")}>
          <button className={"mode-seg" + (playMode === "song" ? " on" : "")} onClick={() => switchMode("song")} title={t("ctrl.song")}>
            <SongWaveIcon size={15} /><span>{t("ctrl.song")}</span>
          </button>
          <button className={"mode-seg" + (playMode === "score" ? " on" : "")} onClick={() => switchMode("score")} title={t("ctrl.score")}>
            <ScoreLinesIcon size={15} /><span>{t("ctrl.score")}</span>
          </button>
        </div>
      )}

      {hasChart && (
        <button className="settings-btn save-btn" onClick={() => { setOpen(true); if (user) startSave(); }}
          title={t("acct.save")} aria-label={t("acct.save")}>
          <SaveIcon size={17} />
        </button>
      )}

      <div className="settings-wrap" ref={wrapRef}>
        <button
          className={"settings-btn menu-btn" + (open ? " on" : "")}
          onClick={() => setOpen((o) => !o)}
          title={t("ctrl.settings")} aria-label={t("ctrl.settings")} aria-expanded={open}
        >
          <MenuIcon size={22} />
        </button>

        {open && (
          <div className="settings-menu" role="menu">
            <div className="settings-acct">
              <span className="ctrl-label">{t("acct.account")}</span>
              {user ? (
                <>
                  <div className="acct-user">{user.name}</div>
                  {saving ? (
                    <div className="save-form">
                      <input className="save-input" value={saveTitle} autoFocus
                        placeholder={t("save.title_ph")}
                        onChange={(e) => setSaveTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") confirmSave(); else if (e.key === "Escape") setSaving(false); }} />
                      <div className="acct-actions">
                        <button className="acct-btn" onClick={confirmSave}>{t("save.confirm")}</button>
                        <button className="acct-btn ghost" onClick={() => setSaving(false)}>{t("save.cancel")}</button>
                      </div>
                    </div>
                  ) : (
                    <div className="acct-actions">
                      {hasChart && <button className="acct-btn" onClick={startSave}>{t("acct.save")}</button>}
                      <button className="acct-btn" onClick={() => { openScores(true); setOpen(false); }}>{t("acct.myscores")}</button>
                      <button className="acct-btn ghost" onClick={() => logout()}>{t("acct.logout")}</button>
                    </div>
                  )}
                  {saveMsg && <div className="acct-msg">{saveMsg}</div>}
                </>
              ) : (
                <>
                  <div ref={gbtnRef} className="gbtn" />
                  <div className="acct-hint">{t("acct.login_hint")}</div>
                </>
              )}
            </div>
            <div className="settings-divider" />
            <div className="settings-row">
              <span className="ctrl-label"><VolumeIcon size={15} /> {t("ctrl.volume")}</span>
              <input type="range" min={0} max={100} value={volume}
                style={{ ["--p" as string]: `${volume}%` }}
                onChange={(e) => { const v = +e.target.value; setVolume(v); c.song.setVolume(v); c.score.setMasterVolume(v / 100); }} />
              <span className="ctrl-val">{volume}</span>
            </div>
            <div className="settings-row">
              <span className="ctrl-label"><TargetIcon size={15} /> {t("ctrl.cursorcomp")}</span>
              <input type="range" min={0} max={300} step={10} value={cursorComp}
                style={{ ["--p" as string]: `${(cursorComp / 300) * 100}%` }}
                onChange={(e) => { const ms = +e.target.value; setCursorComp(ms); c.song.setCursorComp(ms / 1000); }} />
              <span className="ctrl-val">{cursorComp}ms</span>
            </div>
            <div className="settings-lang">
              <span className="ctrl-label">{t("ctrl.language")}</span>
              <div className="lang-seg">
                {LANGS.map((l) => (
                  <button key={l.code} className={"lang-opt" + (lang === l.code ? " on" : "")}
                    onClick={() => setLang(l.code)}>{l.label}</button>
                ))}
              </div>
            </div>
            <div className="settings-divider" />
            <nav className="settings-nav">
              <a className="nav-link" href="/popular">{t("footer.popular")}</a>
              <a className="nav-link" href="/about">{t("footer.about")}</a>
              <a className="nav-link" href="/contact">{t("footer.contact")}</a>
              <a className="nav-link" href="/terms">{t("footer.terms")}</a>
              <a className="nav-link" href="/privacy">{t("footer.privacy")}</a>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}
